/**
 * Folk Coffee Club — Homepage JS
 * GSAP + ScrollTrigger powered animations
 * Theme switcher, drawers, mega menus, preloader, scroll effects
 */
(function() {
  'use strict';

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ========== THEME SWITCHER ========== */
  const Theme = {
    init() {
      this.btns = $$('[data-theme-btn]');
      this.set(localStorage.getItem('folk-theme') || 'light');
      this.btns.forEach(b => b.addEventListener('click', () => this.set(b.dataset.themeBtn)));
    },
    set(t) {
      document.documentElement.dataset.theme = t;
      localStorage.setItem('folk-theme', t);
      this.btns.forEach(b => b.classList.toggle('active', b.dataset.themeBtn === t));
    }
  };

  /* ========== HAMBURGER DRAWER ========== */
  const Drawer = {
    init() {
      this.el = $('[data-drawer]');
      this.toggle = $('[data-drawer-toggle]');
      this.overlay = $('[data-overlay]');
      if (!this.el) return;
      this.toggle?.addEventListener('click', e => { e.preventDefault(); this.open(); });
      this.overlay?.addEventListener('click', () => { this.hide(); Cart.hide(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') { this.hide(); Cart.hide(); } });
    },
    open() {
      this.el.classList.add('active');
      this.overlay?.classList.add('active');
      document.body.classList.add('body-locked');
      if (typeof gsap !== 'undefined' && !prefersReducedMotion()) {
        gsap.fromTo('.drawer__section', { x: -30, opacity: 0 }, { x: 0, opacity: 1, stagger: 0.08, duration: 0.5, ease: 'power3.out', delay: 0.15 });
      }
    },
    hide() {
      this.el.classList.remove('active');
      this.overlay?.classList.remove('active');
      document.body.classList.remove('body-locked');
    }
  };

  /* ========== CART DRAWER ========== */
  const Cart = {
    init() {
      this.el = $('[data-cart-drawer]');
      this.toggle = $('[data-cart-toggle]');
      this.close = $('[data-cart-close]');
      this.overlay = $('[data-cart-overlay]');
      this.tabs = $$('[data-cart-tab]');
      this.contents = $$('[data-cart-content]');
      if (!this.el) return;

      this.toggle?.addEventListener('click', e => { e.preventDefault(); this.open(); });
      this.close?.addEventListener('click', () => this.hide());
      this.overlay?.addEventListener('click', () => this.hide());
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this.hide(); });

      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const tabId = tab.dataset.cartTab;
          this.tabs.forEach(t => t.classList.remove('active'));
          this.contents.forEach(c => c.classList.remove('active'));
          tab.classList.add('active');
          $(`[data-cart-content="${tabId}"]`)?.classList.add('active');
        });
      });
    },
    open() {
      this.el.classList.add('active');
      this.overlay?.classList.add('active');
      document.body.classList.add('body-locked');
    },
    hide() {
      this.el.classList.remove('active');
      this.overlay?.classList.remove('active');
      document.body.classList.remove('body-locked');
    }
  };

  /* ========== MEGA MENUS ========== */
  const Mega = {
    init() {
      this.triggers = $$('[data-mega-trigger]');
      this.active = null;
      this.timeout = null;
      this.triggers.forEach(t => {
        const panel = $(`[data-mega-panel="${t.dataset.megaTrigger}"]`);
        if (!panel) return;
        t.addEventListener('mouseenter', () => this.open(t, panel));
        t.addEventListener('mouseleave', () => this.scheduleClose());
        panel.addEventListener('mouseenter', () => this.cancelClose());
        panel.addEventListener('mouseleave', () => this.close());
        t.addEventListener('click', e => { e.preventDefault(); this.active === panel ? this.close() : this.open(t, panel); });
      });
      document.addEventListener('keydown', e => e.key === 'Escape' && this.close());
      this.setupHoverDescriptions();
    },
    setupHoverDescriptions() {
      $$('.mega-menu').forEach(panel => {
        const desc = $('[data-mega-desc]', panel);
        const links = $$('[data-hover-desc]', panel);
        if (!desc || !links.length) return;
        const defaultText = desc.textContent;
        links.forEach(link => {
          link.addEventListener('mouseenter', () => {
            desc.textContent = link.dataset.hoverDesc;
            desc.style.opacity = '1';
            desc.style.color = 'var(--cream)';
          });
          link.addEventListener('mouseleave', () => {
            desc.textContent = defaultText;
            desc.style.opacity = '0.8';
          });
        });
      });
    },
    scheduleClose() { this.timeout = setTimeout(() => this.close(), 150); },
    cancelClose() { clearTimeout(this.timeout); },
    open(trigger, panel) {
      this.cancelClose();
      if (this.active && this.active !== panel) {
        this.active.classList.remove('active');
        this.triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
      }
      this.active = panel;
      trigger.setAttribute('aria-expanded', 'true');

      const rightCol = $('.mega-menu__right', panel);
      if (rightCol) {
        const triggerRect = trigger.getBoundingClientRect();
        rightCol.style.left = triggerRect.left + 'px';
        rightCol.style.right = 'auto';
      }

      panel.classList.add('active');
      if (typeof gsap !== 'undefined' && !prefersReducedMotion()) {
        const cards = $$('.mega-card', panel);
        if (cards.length) gsap.fromTo(cards, { y: 40, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.08, duration: 0.6, ease: 'power3.out', delay: 0.25 });
        const links = $$('.mega-menu__link', panel);
        if (links.length) gsap.fromTo(links, { y: 30, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.06, duration: 0.6, ease: 'power3.out', delay: 0.2 });
        const desc = $('.mega-menu__desc', panel);
        if (desc) gsap.fromTo(desc, { y: 20, opacity: 0 }, { y: 0, opacity: 0.8, duration: 0.5, ease: 'power3.out', delay: 0.3 });
      }
    },
    close() {
      if (!this.active) return;
      this.triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
      this.active.classList.remove('active');
      $$('.mega-menu__link, .mega-card', this.active).forEach(l => { l.style.opacity = ''; l.style.transform = ''; });
      this.active = null;
    }
  };

  /* ========== HERO ========== */
  const Hero = {
    init() {
      this.headline = $('[data-hero-headline]');
    },
    animate() {
      if (prefersReducedMotion() || typeof gsap === 'undefined') { this.show(); return; }
      // Animate H1 in
      gsap.to('[data-hero-headline]', { y: 0, opacity: 1, duration: 1, delay: 0.2, ease: 'power3.out' });
      // Animate CTAs in
      gsap.to('[data-hero-cta]', { y: 0, opacity: 1, stagger: 0.1, duration: 0.6, delay: 0.5, ease: 'power3.out' });
    },
    show() {
      $$('[data-hero-headline], [data-hero-cta]').forEach(el => { el.style.opacity = '1'; el.style.transform = 'none'; });
    }
  };

  /* ========== PRELOADER ========== */
  const Preloader = {
    progress: 0,
    isComplete: false,

    init() {
      this.el = $('[data-preloader]');
      this.video = $('[data-preloader-video]');
      this.logo = $('[data-preloader-logo]');
      this.scrollBtn = $('[data-preloader-scroll]');

      if (!this.el) return;

      // Lock body scroll
      document.body.style.overflow = 'hidden';

      // Check if GSAP is available
      if (typeof gsap === 'undefined') {
        console.warn('GSAP not loaded, skipping preloader animation');
        this.skipPreloader();
        return;
      }

      // Setup wheel-based scroll
      this.setupWheelControl();

      // Scroll button click - skip to complete
      if (this.scrollBtn) {
        this.scrollBtn.addEventListener('click', () => this.complete());
      }
    },

    skipPreloader() {
      this.el.style.display = 'none';
      document.body.style.overflow = '';
      Hero.animate();
    },

    setupWheelControl() {
      // Use wheel event to control preloader progress
      const wheelHandler = (e) => {
        if (this.isComplete) return;
        e.preventDefault();

        const delta = e.deltaY / 600;
        this.progress = Math.max(0, Math.min(1, this.progress + delta));
        this.updateAnimation();

        if (this.progress >= 1) {
          this.complete();
        }
      };

      window.addEventListener('wheel', wheelHandler, { passive: false });

      // Touch support
      let touchStartY = 0;
      window.addEventListener('touchstart', (e) => {
        touchStartY = e.touches[0].clientY;
      }, { passive: true });

      window.addEventListener('touchmove', (e) => {
        if (this.isComplete) return;
        e.preventDefault();

        const touchY = e.touches[0].clientY;
        const delta = (touchStartY - touchY) / 300;
        touchStartY = touchY;

        this.progress = Math.max(0, Math.min(1, this.progress + delta));
        this.updateAnimation();

        if (this.progress >= 1) {
          this.complete();
        }
      }, { passive: false });
    },

    updateAnimation() {
      const p = this.progress;

      // Video expands from small to fullscreen
      const videoProgress = Math.min(1, p / 0.85);
      const videoWidth = 200 + (window.innerWidth - 200) * videoProgress;
      const videoHeight = 140 + (window.innerHeight - 140) * videoProgress;

      gsap.set(this.video, {
        width: videoWidth,
        height: videoHeight,
        borderRadius: 4 * (1 - videoProgress)
      });

      // Logo and registered mark fade out together
      const logoProgress = Math.min(1, p / 0.5);
      gsap.set(this.logo, {
        opacity: 1 - logoProgress,
        y: 60 * logoProgress
      });
      gsap.set('.preloader__registered', {
        opacity: 1 - logoProgress
      });

      // CTA and scroll button fade out
      gsap.set(['.preloader__cta', '.preloader__scroll'], {
        opacity: 1 - Math.min(1, p / 0.4)
      });

      // Background fades after video is nearly fullscreen
      const bgProgress = Math.max(0, (p - 0.7) / 0.3);
      this.el.style.backgroundColor = `rgba(255, 72, 0, ${1 - bgProgress})`;
    },

    complete() {
      if (this.isComplete) return;
      this.isComplete = true;

      // Ensure video is fullscreen
      gsap.set(this.video, {
        width: '100vw',
        height: '100vh',
        borderRadius: 0
      });

      // Fade out the entire preloader smoothly
      gsap.to(this.el, {
        opacity: 0,
        duration: 0.5,
        ease: 'power2.out',
        onComplete: () => {
          this.el.classList.add('complete');
          this.el.style.pointerEvents = 'none';
          document.body.style.overflow = '';
          Hero.animate();
        }
      });
    }
  };

  /* ========== SCROLL ANIMATIONS (GSAP + ScrollTrigger) ========== */
  const ScrollAnimations = {
    init() {
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
      gsap.registerPlugin(ScrollTrigger);
      this.ritualVideo();
      this.ritualCards();
      this.fullbleedShrink();
      this.islandNav();
    },

    ritualVideo() {
      const container = document.getElementById('ritualVideoContainer');
      const wrapper = document.getElementById('ritualVideoWrapper');
      const gradient = document.getElementById('ritualGradient');
      const overlay = document.getElementById('ritualOverlay');
      const intro = document.getElementById('ritualIntro');
      if (!container || !wrapper) return;

      gsap.to(container, { y: '0%', ease: 'none', scrollTrigger: { trigger: '.ritual-pinned', start: 'top bottom', end: 'top top', scrub: 1 } });
      if (intro) gsap.to(intro, { opacity: 0, y: -80, ease: 'none', scrollTrigger: { trigger: '.ritual-pinned', start: 'top 90%', end: 'top 30%', scrub: 1 } });
      gsap.to(container, { paddingLeft: 0, paddingRight: 0, ease: 'none', scrollTrigger: { trigger: '.ritual-pinned', start: 'top 40%', end: 'top top', scrub: 1 } });
      gsap.to(wrapper, { borderTopLeftRadius: '0px', borderTopRightRadius: '0px', ease: 'none', scrollTrigger: { trigger: '.ritual-pinned', start: 'top 40%', end: 'top top', scrub: 1 } });
      if (gradient) gsap.to(gradient, { opacity: 1, ease: 'none', scrollTrigger: { trigger: '.ritual-pinned', start: 'top 20%', end: 'top top', scrub: 1 } });
      if (overlay) gsap.to(overlay, { y: '-50%', ease: 'none', scrollTrigger: { trigger: '.ritual-pinned', start: '8% top', end: '15% top', scrub: 1 } });
    },

    ritualCards() {
      const items = document.querySelectorAll('.ritual-cards__item');
      const images = document.querySelectorAll('.ritual-cards__image');
      if (!items.length) return;
      let active = 0;

      function setActive(index) {
        if (index === active || index < 0 || index >= items.length) return;
        active = index;
        items.forEach((item, i) => item.classList.toggle('is-active', i === index));
        images.forEach((img, i) => img.classList.toggle('is-active', i === index));
      }

      ScrollTrigger.create({
        trigger: '#ritualCards', start: 'top top', end: 'bottom bottom', scrub: 0.5,
        onUpdate: (self) => {
          const newIndex = Math.min(Math.floor(self.progress * items.length), items.length - 1);
          setActive(newIndex);
        }
      });
    },

    fullbleedShrink() {
      const fullbleed = $('.fullbleed');
      if (!fullbleed) return;
      const bg = fullbleed.querySelector('.fullbleed__bg');
      const bgImg = bg?.querySelector('img');
      const content = fullbleed.querySelector('.fullbleed__content');
      if (!bg || !bgImg || !content) return;

      ScrollTrigger.create({
        trigger: fullbleed, start: 'top center', end: 'bottom center', scrub: 0.5,
        onUpdate: (self) => {
          const p = self.progress;
          const inset = Math.min(p * 30, 30);
          bg.style.inset = `${inset}px`;
          bgImg.style.borderRadius = `${inset > 0 ? 16 : 0}px`;
          content.style.paddingLeft = `${32 + inset}px`;
        }
      });
    },

    islandNav() {
      const island = document.querySelector('[data-island-nav]');
      if (!island) return;
      const showAfter = 400;
      const buffer = 50;
      let isVisible = false;
      let ticking = false;

      const updateNav = () => {
        const scrollY = window.scrollY;
        if (!isVisible && scrollY > showAfter) { island.classList.add('visible'); isVisible = true; }
        else if (isVisible && scrollY < showAfter - buffer) { island.classList.remove('visible'); isVisible = false; }
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (!ticking) { requestAnimationFrame(updateNav); ticking = true; }
      }, { passive: true });
    }
  };

  /* ========== SVG FILTER ========== */
  const SVGFilter = {
    init() {
      if (document.getElementById('warp-filter')) return;
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.style.cssText = 'position:absolute;width:0;height:0';
      svg.innerHTML = '<defs><filter id="warp-filter"><feTurbulence type="turbulence" baseFrequency="0.015" numOctaves="3" result="turbulence" seed="5"/><feDisplacementMap in="SourceGraphic" in2="turbulence" scale="12" xChannelSelector="R" yChannelSelector="G"/></filter></defs>';
      document.body.appendChild(svg);
    }
  };

  /* ========== INIT ========== */
  const init = () => {
    Theme.init();
    Drawer.init();
    Cart.init();
    Mega.init();
    Hero.init();
    Preloader.init();
    ScrollAnimations.init();
    SVGFilter.init();
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();
